import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, BookOpen } from 'lucide-react';
import { articlesApi } from '../../lib/api';

export function SeriesNavigation({ currentSlug }) {
  const [seriesList, setSeriesList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSeries = async () => {
      try {
        const res = await articlesApi.getSeries(currentSlug);
        setSeriesList(res.data || []);
      } catch {
        setSeriesList([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSeries();
  }, [currentSlug]);

  if (loading || seriesList.length === 0) return null;

  return (
    <div className="mt-16 space-y-6" data-testid="series-navigation">
      {seriesList.map((series) => (
        <div
          key={series.series_slug}
          className="border border-zinc-800 bg-zinc-900/40"
          data-testid={`series-card-${series.series_slug}`}
        >
          {/* Series Header */}
          <div className="px-6 pt-5 pb-4 border-b border-zinc-800/60">
            <div className="flex items-center gap-2 mb-1">
              <BookOpen className="w-4 h-4 text-[#E63946]" />
              <span className="text-[10px] uppercase tracking-[0.15em] text-[#E63946] font-semibold">
                Series
              </span>
            </div>
            <h3
              className="text-white text-xl mb-1"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              data-testid="series-name"
            >
              {series.series_name}
            </h3>
            {series.series_description && (
              <p className="text-zinc-500 text-xs leading-relaxed">{series.series_description}</p>
            )}
            {/* Progress */}
            <div className="mt-3 flex items-center gap-3">
              <div className="flex-1 h-[3px] bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#E63946] transition-all duration-500"
                  style={{ width: `${(series.current_position / series.total_articles) * 100}%` }}
                  data-testid="series-progress-bar"
                />
              </div>
              <span className="text-zinc-500 text-xs whitespace-nowrap" data-testid="series-progress-text">
                {series.current_position} of {series.total_articles}
              </span>
            </div>
          </div>

          {/* Prev / Next Navigation */}
          <div className="grid grid-cols-2 divide-x divide-zinc-800/60">
            {/* Previous */}
            <div className="p-4">
              {series.prev_article ? (
                <Link
                  to={`/blogs/article/${series.prev_article.slug}`}
                  className="group flex items-start gap-3 hover:opacity-80 transition-opacity"
                  data-testid="series-prev-link"
                >
                  <ChevronLeft className="w-5 h-5 text-zinc-600 group-hover:text-[#E63946] transition-colors mt-0.5 shrink-0" />
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-zinc-600 block mb-1">Previous</span>
                    <span className="text-sm text-zinc-300 group-hover:text-white transition-colors leading-tight block">
                      {series.prev_article.title}
                    </span>
                    {series.prev_article.read_time && (
                      <span className="text-[10px] text-zinc-600 mt-1 block">{series.prev_article.read_time} min read</span>
                    )}
                  </div>
                </Link>
              ) : (
                <div className="flex items-center gap-3 opacity-30">
                  <ChevronLeft className="w-5 h-5 text-zinc-700 shrink-0" />
                  <span className="text-xs text-zinc-700">Start of series</span>
                </div>
              )}
            </div>

            {/* Next */}
            <div className="p-4">
              {series.next_article ? (
                <Link
                  to={`/blogs/article/${series.next_article.slug}`}
                  className="group flex items-start gap-3 justify-end text-right hover:opacity-80 transition-opacity"
                  data-testid="series-next-link"
                >
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-zinc-600 block mb-1">Next in Series</span>
                    <span className="text-sm text-zinc-300 group-hover:text-white transition-colors leading-tight block">
                      {series.next_article.title}
                    </span>
                    {series.next_article.read_time && (
                      <span className="text-[10px] text-zinc-600 mt-1 block">{series.next_article.read_time} min read</span>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-[#E63946] transition-colors mt-0.5 shrink-0" />
                </Link>
              ) : (
                <div className="flex items-center gap-3 justify-end opacity-30">
                  <span className="text-xs text-zinc-700">End of series</span>
                  <ChevronRight className="w-5 h-5 text-zinc-700 shrink-0" />
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
